<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import TableVisualization from '../TableVisualization/TableVisualization.vue';
import { GuestClass } from '../../types/guest';
import { Table, TableWithGuests } from '../../types/table';

interface Props {
  tables: TableWithGuests[];
  selectedGuestId: string | null;
  selectedTable: Table | null;
  onGuestClick: (guest: GuestClass) => void;
  onTableClick: (table: Table) => void;
  onTablePositionChange: (tableIndex: number, position: { x: number; y: number }) => void;
  onAddGuest: (tableIndex: number, seatIndex: number) => void;
  onSeatCountChange: (tableIndex: number, count: number) => void;
}

const props = defineProps<Props>();

const isTableSelected = (table: Table) => {
  if (!props.selectedTable) return false;
  return (
    table.shape === props.selectedTable.shape &&
    table.position.x === props.selectedTable.position.x &&
    table.position.y === props.selectedTable.position.y &&
    table.seatCount === props.selectedTable.seatCount &&
    table.label === props.selectedTable.label &&
    table.number === props.selectedTable.number &&
    table.length === props.selectedTable.length &&
    table.width === props.selectedTable.width
  );
};

const canvasRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const dragStartPos = ref({ x: 0, y: 0 });
const currentDragTable = ref<number | null>(null);
const scale = ref(1);
const minScale = 0.2;
const maxScale = 3;
const canvasPosition = ref({ x: 0, y: 0 });
const isPanning = ref(false);
const canvasDimensions = ref({ width: 0, height: 0 });
let animationFrameId: number | undefined;

const emit = defineEmits<{
  (
    e: 'canvas-resize',
    dimensions: {
      width: number;
      height: number;
      position: { x: number; y: number };
      scale: number;
      visibleCenter: { x: number; y: number };
    },
  ): void;
  (e: 'showUnassignedGuests', seatIndex: number): void;
}>();

const calculateVisibleCenter = () => {
  if (!canvasRef.value) return { x: 0, y: 0 };

  const rect = canvasRef.value.getBoundingClientRect();
  // Calculate center in viewport coordinates
  const viewportCenterX = rect.width / 2;
  const viewportCenterY = rect.height / 2;

  // Convert to canvas coordinates
  const centerX = (viewportCenterX - canvasPosition.value.x) / scale.value;
  const centerY = (viewportCenterY - canvasPosition.value.y) / scale.value;

  return { x: centerX, y: centerY };
};

const updateCanvasDimensions = () => {
  if (canvasRef.value) {
    const rect = canvasRef.value.getBoundingClientRect();
    const dimensions = {
      width: rect.width,
      height: rect.height,
      position: { ...canvasPosition.value },
      scale: scale.value,
      visibleCenter: calculateVisibleCenter(),
    };
    canvasDimensions.value = {
      width: rect.width,
      height: rect.height,
    };
    emit('canvas-resize', dimensions);
  }
};

// Add watchers for position and scale changes
watch(
  [canvasPosition, scale],
  () => {
    updateCanvasDimensions();
  },
  { deep: true },
);

// Also update dimensions when panning
watch(isPanning, (newValue) => {
  if (!newValue) {
    updateCanvasDimensions();
  }
});

const handleTableClick = (table: Table, e: MouseEvent | TouchEvent) => {
  e.preventDefault();
  e.stopPropagation();

  if (!isDragging.value) {
    props.onTableClick(table);
  }
};

const handleTableMouseDown = (e: MouseEvent, tableIndex: number) => {
  e.preventDefault();
  e.stopPropagation();

  isDragging.value = true;
  currentDragTable.value = tableIndex;
  const table = props.tables[tableIndex];
  if (table) {
    const canvasRect = canvasRef.value?.getBoundingClientRect();
    if (canvasRect) {
      const canvasX = (e.clientX - canvasRect.left - canvasPosition.value.x) / scale.value;
      const canvasY = (e.clientY - canvasRect.top - canvasPosition.value.y) / scale.value;

      dragStartPos.value = {
        x: canvasX - table.position.x,
        y: canvasY - table.position.y,
      };
    }
  }
};

const handleTableTouchStart = (e: TouchEvent, tableIndex: number) => {
  e.preventDefault();
  e.stopPropagation();

  isDragging.value = true;
  currentDragTable.value = tableIndex;
  const table = props.tables[tableIndex];
  if (table) {
    const canvasRect = canvasRef.value?.getBoundingClientRect();
    if (canvasRect && e.touches.length > 0) {
      const touch = e.touches[0];
      if (touch) {
        const canvasX = (touch.clientX - canvasRect.left - canvasPosition.value.x) / scale.value;
        const canvasY = (touch.clientY - canvasRect.top - canvasPosition.value.y) / scale.value;

        dragStartPos.value = {
          x: canvasX - table.position.x,
          y: canvasY - table.position.y,
        };
      }
    }
  }
};

const handleTableTouchEnd = (e: TouchEvent, table: Table) => {
  e.preventDefault();
  e.stopPropagation();

  if (!isDragging.value) {
    props.onTableClick(table);
  }
  isDragging.value = false;
  currentDragTable.value = null;
};

const handleMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || currentDragTable.value === null || !canvasRef.value) return;

  if (animationFrameId !== undefined) {
    cancelAnimationFrame(animationFrameId);
  }

  animationFrameId = requestAnimationFrame(() => {
    const canvasRect = canvasRef.value?.getBoundingClientRect();
    if (!canvasRect) return;

    // Calculate new position taking into account canvas scale and position
    const canvasX = (e.clientX - canvasRect.left - canvasPosition.value.x) / scale.value;
    const canvasY = (e.clientY - canvasRect.top - canvasPosition.value.y) / scale.value;

    const newX = canvasX - dragStartPos.value.x;
    const newY = canvasY - dragStartPos.value.y;

    const tableIndex = currentDragTable.value;
    if (tableIndex !== null) {
      props.onTablePositionChange(tableIndex, { x: newX, y: newY });
    }
    animationFrameId = undefined;
  });
};

const handleMouseUp = () => {
  isDragging.value = false;
  currentDragTable.value = null;
};

const handleWheel = (e: WheelEvent) => {
  e.preventDefault();
  const delta = e.deltaY > 0 ? -0.1 : 0.1;
  const newScale = Math.min(Math.max(scale.value + delta, minScale), maxScale);
  scale.value = newScale;
};

const zoomIn = () => {
  scale.value = Math.min(scale.value + 0.1, maxScale);
};

const zoomOut = () => {
  scale.value = Math.max(scale.value - 0.1, minScale);
};

const resetZoom = () => {
  scale.value = 1;
};

const handleCanvasMouseDown = (e: MouseEvent) => {
  if (e.target === canvasRef.value || e.target === canvasRef.value?.firstElementChild) {
    isPanning.value = true;
    const canvasRect = canvasRef.value?.getBoundingClientRect();
    if (canvasRect) {
      dragStartPos.value = {
        x: e.clientX - canvasPosition.value.x,
        y: e.clientY - canvasPosition.value.y,
      };
    }
  }
};

const handleCanvasMouseMove = (e: MouseEvent) => {
  if (isPanning.value) {
    if (animationFrameId !== undefined) {
      cancelAnimationFrame(animationFrameId);
    }

    animationFrameId = requestAnimationFrame(() => {
      const newX = e.clientX - dragStartPos.value.x;
      const newY = e.clientY - dragStartPos.value.y;

      canvasPosition.value = {
        x: newX,
        y: newY,
      };
      animationFrameId = undefined;
    });
  }
};

const handleCanvasMouseUp = () => {
  isPanning.value = false;
};

const resetCanvasPosition = () => {
  canvasPosition.value = { x: 0, y: 0 };
  scale.value = 1;
};

const handleTouchMove = (e: TouchEvent) => {
  if (!isDragging.value || currentDragTable.value === null || !canvasRef.value || e.touches.length === 0) return;

  if (animationFrameId !== undefined) {
    cancelAnimationFrame(animationFrameId);
  }

  animationFrameId = requestAnimationFrame(() => {
    const canvasRect = canvasRef.value?.getBoundingClientRect();
    if (!canvasRect) return;

    const touch = e.touches[0];
    if (!touch) return;

    const canvasX = (touch.clientX - canvasRect.left - canvasPosition.value.x) / scale.value;
    const canvasY = (touch.clientY - canvasRect.top - canvasPosition.value.y) / scale.value;

    const newX = canvasX - dragStartPos.value.x;
    const newY = canvasY - dragStartPos.value.y;

    const tableIndex = currentDragTable.value;
    if (tableIndex !== null) {
      props.onTablePositionChange(tableIndex, { x: newX, y: newY });
    }
    animationFrameId = undefined;
  });
};

const handleCanvasTouchStart = (e: TouchEvent) => {
  if (e.target === canvasRef.value || e.target === canvasRef.value?.firstElementChild) {
    isPanning.value = true;
    const canvasRect = canvasRef.value?.getBoundingClientRect();
    if (canvasRect && e.touches.length > 0) {
      const touch = e.touches[0];
      if (touch) {
        dragStartPos.value = {
          x: touch.clientX - canvasPosition.value.x,
          y: touch.clientY - canvasPosition.value.y,
        };
      }
    }
  }
};

const handleCanvasTouchMove = (e: TouchEvent) => {
  if (isPanning.value && e.touches.length > 0) {
    if (animationFrameId !== undefined) {
      cancelAnimationFrame(animationFrameId);
    }

    animationFrameId = requestAnimationFrame(() => {
      const touch = e.touches[0];
      if (touch) {
        const newX = touch.clientX - dragStartPos.value.x;
        const newY = touch.clientY - dragStartPos.value.y;

        canvasPosition.value = {
          x: newX,
          y: newY,
        };
      }
      animationFrameId = undefined;
    });
  }
};

const handleCanvasTouchEnd = () => {
  isPanning.value = false;
};

const handlePinchZoom = (e: TouchEvent) => {
  if (e.touches.length === 2) {
    e.preventDefault();
    const touch1 = e.touches[0];
    const touch2 = e.touches[1];

    if (touch1 && touch2) {
      const currentDistance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);

      if (lastPinchDistance.value) {
        const scaleChange = currentDistance / lastPinchDistance.value;
        const newScale = Math.min(Math.max(scale.value * scaleChange, minScale), maxScale);
        scale.value = newScale;
      }

      lastPinchDistance.value = currentDistance;
    }
  }
};

const lastPinchDistance = ref<number | null>(null);

const handlePinchEnd = () => {
  lastPinchDistance.value = null;
};

const handleShowUnassignedGuests = (seatIndex: number) => {
  emit('showUnassignedGuests', seatIndex);
};

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('mousemove', handleCanvasMouseMove);
  window.addEventListener('mouseup', handleCanvasMouseUp);
  window.addEventListener('touchmove', handleTouchMove);
  window.addEventListener('touchend', handleCanvasTouchEnd);
  window.addEventListener('touchmove', handleCanvasTouchMove);
  window.addEventListener('touchend', handleCanvasTouchEnd);
  window.addEventListener('touchmove', handlePinchZoom);
  window.addEventListener('touchend', handlePinchEnd);
  window.addEventListener('resize', updateCanvasDimensions);
  updateCanvasDimensions();
});

onUnmounted(() => {
  if (animationFrameId !== undefined) {
    cancelAnimationFrame(animationFrameId);
  }
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  window.removeEventListener('mousemove', handleCanvasMouseMove);
  window.removeEventListener('mouseup', handleCanvasMouseUp);
  window.removeEventListener('touchmove', handleTouchMove);
  window.removeEventListener('touchend', handleCanvasTouchEnd);
  window.removeEventListener('touchmove', handleCanvasTouchMove);
  window.removeEventListener('touchend', handleCanvasTouchEnd);
  window.removeEventListener('touchmove', handlePinchZoom);
  window.removeEventListener('touchend', handlePinchEnd);
  window.removeEventListener('resize', updateCanvasDimensions);
});
</script>

<template>
  <div class="table-canvas-container">
    <div class="table-canvas__controls">
      <button
        class="table-canvas__control-button"
        @click="zoomIn"
        @touchstart.prevent="zoomIn"
      >
        <i class="pi pi-plus"></i>
      </button>
      <button
        class="table-canvas__control-button"
        @click="resetZoom"
        @touchstart.prevent="resetZoom"
      >
        <i class="pi pi-refresh"></i>
      </button>
      <button
        class="table-canvas__control-button"
        @click="zoomOut"
        @touchstart.prevent="zoomOut"
      >
        <i class="pi pi-minus"></i>
      </button>
      <button
        class="table-canvas__control-button"
        @click="resetCanvasPosition"
      >
        <i class="pi pi-home"></i>
      </button>
    </div>
    <div
      ref="canvasRef"
      class="table-canvas"
      @mousedown="handleCanvasMouseDown"
      @touchstart="handleCanvasTouchStart"
      @wheel.prevent="handleWheel"
    >
      <div
        class="table-canvas__content"
        :style="{
          transform: `translate3d(${canvasPosition.x}px, ${canvasPosition.y}px, 0) scale3d(${scale}, ${scale}, 1)`,
          cursor: isPanning ? 'grabbing' : 'grab',
        }"
      >
        <div
          v-for="(table, index) in tables"
          :key="index"
          class="table-container"
          :class="{ 'table-container--selected': isTableSelected(table) }"
          :style="{
            transform: `translate3d(${table.position.x}px, ${table.position.y}px, 0)`,
            cursor: isDragging && currentDragTable === index ? 'grabbing' : 'grab',
          }"
          @mousedown="(e) => handleTableMouseDown(e, index)"
          @touchstart="(e) => handleTableTouchStart(e, index)"
          @touchend="(e) => handleTableTouchEnd(e, table)"
          @click="(e) => handleTableClick(table, e)"
        >
          <TableVisualization
            :guests="table.guests"
            :table-shape="table.shape"
            :table-number="index + 1"
            :selected-guest-id="selectedGuestId"
            :is-selected="isTableSelected(table)"
            :seat-count="table.seatCount || 0"
            :length="table.length || 0"
            :width="table.width || 0"
            @guest-click="(guest) => props.onGuestClick(guest)"
            @add-guest="(seatIndex) => props.onAddGuest(index, seatIndex)"
            @seat-count-change="(count) => onSeatCountChange(index, count)"
            @show-unassigned-guests="handleShowUnassignedGuests"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.table-canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  touch-action: none;
}

.table-canvas {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 600px;
  background-color: #f8f8f8;
  border: 2px dashed #834920;
  border-radius: 8px;
  overflow: hidden;
  transform-origin: center center;
  cursor: grab;
  user-select: none;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
}

.table-canvas__content {
  position: absolute;
  width: 100%;
  height: 100%;
  transition: transform 0.1s ease;
  user-select: none;
  transform-origin: center center;
  will-change: transform;
  backface-visibility: hidden;
  perspective: 1000px;
}

.table-canvas__controls {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  background-color: white;
  padding: 0.5rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgb(0 0 0 / 10%);
  z-index: 10;
}

.table-canvas__control-button {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #834920;
  background-color: white;
  color: #834920;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  touch-action: manipulation;

  &:hover {
    background-color: #f2e9e4;
  }

  i {
    font-size: 1rem;
  }
}

.table-container {
  position: absolute;
  transition: transform 0.1s ease;
  user-select: none;
  will-change: transform;
  backface-visibility: hidden;
  perspective: 1000px;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  &:active {
    cursor: grabbing;
  }

  &--selected {
    outline: 2px solid $brown-400;
    outline-offset: 2px;
  }

  &:hover {
    transform: scale(1.02);
  }
}
</style>
